import React from 'react';
import { useRouteError, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

const ErrorPage: React.FC = () => {
	const error = useRouteError() as any;
	const navigate = useNavigate();

	const handleReload = () => {
		window.location.reload();
	};

	const handleGoHome = () => {
		navigate('/');
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '100vh',
				width: '100vw',
				background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
				color: '#111827',
				p: 4,
				overflow: 'hidden',
				position: 'relative'
			}}
		>
			{/* Decorative background elements */}
			<Box
				sx={{
					position: 'absolute',
					top: '-10%',
					left: '-5%',
					width: '40vw',
					height: '40vw',
					borderRadius: '50%',
					background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, rgba(255,255,255,0) 70%)',
					filter: 'blur(60px)',
					zIndex: 0,
				}}
			/>
			<Box
				sx={{
					position: 'absolute',
					bottom: '-10%',
					right: '-5%',
					width: '50vw',
					height: '50vw',
					borderRadius: '50%',
					background: 'radial-gradient(circle, rgba(139,92,246,0.1) 0%, rgba(255,255,255,0) 70%)',
					filter: 'blur(80px)',
					zIndex: 0,
				}}
			/>

			<Paper
				elevation={24}
				sx={{
					p: { xs: 4, md: 6 },
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					maxWidth: 650,
					width: '100%',
					textAlign: 'center',
					borderRadius: 4,
					background: '#ffffff',
					zIndex: 1,
					boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
				}}
			>
				<Box
					sx={{
						width: 100,
						height: 100,
						borderRadius: '50%',
						background: 'rgba(139, 92, 246, 0.1)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						mb: 3,
					}}
				>
					<ErrorOutlineIcon sx={{ fontSize: 50, color: '#8B5CF6' }} />
				</Box>
				
				<Typography variant="h3" gutterBottom sx={{ fontWeight: 800, letterSpacing: '-0.5px', color: '#111827' }}>
					System Malfunction
				</Typography>
				
				<Typography variant="h6" sx={{ mb: 4, color: '#4B5563', fontWeight: 400, lineHeight: 1.6 }}>
					We've encountered an unexpected error in the editor. Our systems have logged the issue.
				</Typography>
				
				{error && (
					<Box
						sx={{
							background: '#F3F4F6',
							p: 3,
							borderRadius: 2,
							width: '100%',
							mb: 5,
							overflowX: 'auto',
							textAlign: 'left',
							border: '1px solid #E5E7EB',
						}}
					>
						<Typography variant="body2" component="pre" sx={{ m: 0, color: '#DC2626', fontFamily: 'monospace', fontSize: '0.85rem' }}>
							{error.statusText || error.message || String(error)}
						</Typography>
						{error.stack && (
							<Typography variant="caption" component="pre" sx={{ mt: 1, m: 0, color: '#6B7280', fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
								{error.stack}
							</Typography>
						)}
					</Box>
				)}

				<Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', justifyContent: 'center' }}>
					<Button 
						variant="contained" 
						size="large"
						startIcon={<RefreshIcon />}
						onClick={handleReload}
						sx={{ 
							background: '#8B5CF6',
							color: '#ffffff',
							fontWeight: 600,
							px: 4,
							py: 1.5,
							borderRadius: 3,
							textTransform: 'none',
							fontSize: '1.1rem',
							boxShadow: '0 4px 14px 0 rgba(139, 92, 246, 0.39)',
							'&:hover': {
								background: '#7C3AED',
								boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)',
							}
						}}
					>
						Reload Editor
					</Button>
					<Button 
						variant="outlined" 
						size="large"
						startIcon={<HomeIcon />}
						onClick={handleGoHome}
						sx={{ 
							borderColor: '#E5E7EB',
							color: '#4B5563',
							fontWeight: 600,
							px: 4,
							py: 1.5,
							borderRadius: 3,
							textTransform: 'none',
							fontSize: '1.1rem',
							'&:hover': {
								borderColor: '#D1D5DB',
								background: '#F9FAFB',
							}
						}}
					>
						Back to Gallery
					</Button>
				</Box>
			</Paper>
		</Box>
	);
};

export default ErrorPage;
